import { useMemo } from 'react';
import { ReviewMatch } from './review-match';
import { StagedEntityCard } from './staged-entity-card';
import { NoMatchCard } from './no-match-card';
import { useAliasClusterStates } from './reconcile/use-alias-pill-states';
import { aliasableEntityIds } from './reconcile/alias-entity-ids';
import { isDeduplicatedResult, suggestionLabels } from './utils';
import type { PartyState } from './party-status';
import type { StructuredPartyDetail, SuggestionSummary } from './types';
import style from './party-detail.module.scss';

interface PartyMatchesPaneProps {
  detail: StructuredPartyDetail;
  state: PartyState;
  validMatches: SuggestionSummary[];
  selectedSuggestionId: number | null;
  pending: boolean;
  onSelectSuggestion: (id: number) => void;
  onEditStaged: () => void;
  onCanonize: () => void;
  onSelectEntityMatch: () => void;
}

/**
 * A party completed by an Entities-screen dedupe has no scored suggestion on
 * the wrapper (they were superseded/repointed by the merge), so the pane would
 * otherwise fall through to the "No match found" card. A confirmed match is
 * synthesized from the surviving canonical, keeping the confidence of the
 * scored suggestion a reconcile preserves.
 *
 * @param {StructuredPartyDetail} detail - The party.
 * @return {SuggestionSummary | null} The synthesized confirmed match, or null when the party was not completed by a dedupe.
 */
function dedupeMatchFor(
  detail: StructuredPartyDetail
): SuggestionSummary | null {
  if (
    detail.status !== 'complete' ||
    !detail.resolvedViaDedupe ||
    !detail.confirmedEntity
  ) {
    return null;
  }
  const scored = detail.suggestions.find(
    (s) => s.suggestedEntity?.id === detail.confirmedEntity?.id
  );
  return {
    id: -1,
    status: 'confirmed',
    isAiRecommendation: false,
    userSelected: false,
    confidenceScore: scored?.confidenceScore ?? null,
    confidenceFactors: scored?.confidenceFactors ?? [],
    reviewReason: null,
    suggestedEntity: detail.confirmedEntity,
  };
}

/**
 * The left tile (the staged entity, or the Deduplicated Result an In Progress
 * party acts on) beside the party's matches.
 *
 * @param {PartyMatchesPaneProps} props - The party, its state and matches, and the selection and edit callbacks.
 * @return {JSX.Element} The staged tile beside the match tiles.
 */
export function PartyMatchesPane({
  detail,
  state,
  validMatches,
  selectedSuggestionId,
  pending,
  onSelectSuggestion,
  onEditStaged,
  onCanonize,
  onSelectEntityMatch,
}: PartyMatchesPaneProps) {
  const {
    isProvisional,
    matchLocked,
    resolvedViaReconcile,
    hasUnresolvedAddressFlag,
  } = state;
  const dedupeMatch = useMemo(() => dedupeMatchFor(detail), [detail]);
  const labels = useMemo(
    () => suggestionLabels(detail.suggestions),
    [detail.suggestions]
  );

  // Alias-cluster info across the full visible set, so the "Group N (+ Others)"
  // pill state is correct. Retired entities are excluded (aliasableEntityIds).
  const aliasStates = useAliasClusterStates(
    aliasableEntityIds([
      detail.stagedEntity,
      detail.currentAnchor,
      dedupeMatch?.suggestedEntity,
      ...validMatches.map((s) => s.displayEntity ?? s.suggestedEntity),
    ])
  );

  // The canonized (user-created) match renders as the leftmost tile.
  const orderedMatches = [
    ...validMatches.filter((s) => s.userCreated),
    ...validMatches.filter((s) => !s.userCreated),
  ];

  const anchorTile =
    isProvisional && isDeduplicatedResult(detail) ? detail.currentAnchor : null;
  const leftEntityId = (anchorTile ?? detail.stagedEntity)?.id;

  /**
   * The dedupe result, the no-match card, or the party's match tiles.
   *
   * @return {JSX.Element | JSX.Element[]} The match pane contents.
   */
  const renderMatches = () => {
    if (dedupeMatch) {
      // Completed by a dedupe into a canonical: the surviving entity is the
      // truth, even if a pending suggestion lingers on the retired wrapper (an
      // undo-remnant kept so undo can restore it).
      return (
        <ReviewMatch
          suggestion={dedupeMatch}
          label="Deduplicated Result"
          resolved
          selected
          disabled
          correctBadge={resolvedViaReconcile}
          aliasInfo={
            dedupeMatch.suggestedEntity
              ? aliasStates.get(dedupeMatch.suggestedEntity.id)
              : undefined
          }
        />
      );
    }
    if (orderedMatches.length === 0) {
      return (
        <NoMatchCard
          onSelectEntityMatch={matchLocked ? undefined : onSelectEntityMatch}
        />
      );
    }
    return orderedMatches.map((s) => {
      const tileEntity = s.displayEntity ?? s.suggestedEntity;
      return (
        <ReviewMatch
          key={s.id}
          suggestion={s}
          label={labels.get(s.id) ?? ''}
          selected={selectedSuggestionId === s.id}
          disabled={pending || matchLocked}
          correctBadge={resolvedViaReconcile}
          onSelect={matchLocked ? undefined : () => onSelectSuggestion(s.id)}
          aliasInfo={tileEntity ? aliasStates.get(tileEntity.id) : undefined}
        />
      );
    });
  };

  return (
    <div className={style.matchesRow}>
      <div className={style.stagedPane}>
        <StagedEntityCard
          party={detail}
          anchor={anchorTile}
          onEditClick={onEditStaged}
          onCanonizeClick={onCanonize}
          canonizeDisabled={pending || hasUnresolvedAddressFlag || matchLocked}
          aliasInfo={leftEntityId ? aliasStates.get(leftEntityId) : undefined}
        />
      </div>
      <div className={style.matchesPane}>{renderMatches()}</div>
    </div>
  );
}
